// =============================================================================
// ui/charts/EquityCurve.tsx — the cumulative P&L curve.
// -----------------------------------------------------------------------------
// Relocated from the old client's `renderEquityCurve`, geometry unchanged.
//
// ── 🚨 `preserveAspectRatio="none"` — THE ONE THING TO GET RIGHT ──
//
// The old client stretches this SVG to fill its container, which means the
// viewBox's x and y units scale INDEPENDENTLY. Two consequences, and both were
// flagged before writing a line of this:
//
//   • STROKES DISTORT. A 1.5px line becomes 4px wide and 0.7px tall on a wide
//     container. `vector-effect="non-scaling-stroke"` is what fixes it, and it
//     is on every stroked element here, not just the main path.
//   • TEXT DISTORTS TOO, and there is no `vector-effect` for that. So the axis
//     labels are NOT inside this SVG — they are HTML positioned over it. That is
//     also why the labels can use the app's font stack instead of an SVG one.
//
// ⚠ If this ever changes to `xMidYMid meet`, the chart stops filling its card
// and the label positioning below becomes wrong. The two decisions are one.
//
// ── THE ZERO SPLIT ──
//
// The fill and the line are BLUE above zero and RED below it, split by a
// gradient stop at the zero line's fractional height. Not two paths — one path
// with a two-stop gradient, so the crossing is exact rather than approximated at
// whichever point happens to straddle it.
// =============================================================================

import { useId, useState } from 'react';
import { buildScales, nearestIndex, type EquityPoint } from '../../selectors/dashGauges';

export interface EquityCurveProps {
  points: readonly EquityPoint[];
  /** Formats the y-axis ticks and the hover readout. */
  fmtY: (v: number) => string;
  height?: number;
}

/** `2026-07-31` → `Jul 31`. */
function axisDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

export function EquityCurve({ points, fmtY, height = 260 }: EquityCurveProps) {
  // ⚠ `useId`, not a module counter. Two curves on one page (the dashboard and a
  // pattern modal over it) would otherwise share a gradient id, and the second
  // mount would silently restyle the first.
  const gid = useId().replace(/:/g, '');
  /**
   * Which point the cursor is over, or null.
   *
   * ⚠ AN INDEX, NOT THE POINT. `points` is rebuilt on every range change, so
   * holding the object would pin a stale one and the readout would describe a
   * trade that is no longer in the window.
   */
  const [hover, setHover] = useState<number | null>(null);

  if (!points.length) {
    return <p className="muted">No trades in this window to chart.</p>;
  }

  const values = points.map((p) => p.equity);
  const s = buildScales(values, { ...POINTS_BOX, height });
  const { xAt, yAt, box } = s;
  const cw = box.width - box.padL - box.padR;
  const ch = box.height - box.padT - box.padB;

  const line = points.map((p, i) => `${xAt(i).toFixed(1)},${yAt(p.equity).toFixed(1)}`).join(' ');
  const zeroY = yAt(0);
  // Where zero sits as a fraction of plot height — the gradient's colour stop.
  const zeroFrac = Math.max(0, Math.min(1, (zeroY - box.padT) / ch));

  /**
   * Draw at most ~120 dots. Beta’s rule and beta’s number.
   *
   * ⚠ Only the DRAWN dots thin. The hover overlay maps to every point, so a
   * trade with no dot is still readable — which is why thinning is honest here
   * and would not be if the dots were the only way to reach a point.
   */
  const multiDay = new Set(points.map((p) => p.date)).size > 1;
  const dotStep = points.length <= 120 ? 1 : Math.ceil(points.length / 120);
  const hovered = hover == null ? null : (points[hover] ?? null);

  /**
   * ⚠ TICKS OUTSIDE THE PLOT ARE DROPPED, and this is not cosmetic tidying —
   * it is a real difference between an SVG chart and this hybrid one.
   *
   * `niceTicks` deliberately returns a range WIDER than the data (it floors and
   * ceils to round numbers), so the top tick often sits above `padT`. In beta
   * that label is an SVG `<text>` and the viewport clips it. Here the labels are
   * HTML positioned over the chart, so nothing clips them — an out-of-range
   * `+$6,000` would render floating above a curve that never reaches $4,400.
   *
   * Caught on the side-by-side: beta's axis topped out at +$4,000 and this one
   * printed +$6,000.
   *
   * ⚠ THE BOUND IS THE VIEWBOX, NOT THE PLOT AREA, and the difference is visible
   * on Josh's book. Beta shows `-$6,000` — below the curve's lowest point, but
   * only far enough out to land inside the 36px bottom padding — while `+$6,000`
   * overshoots the top by more than `padT` and falls outside the SVG entirely.
   * Clipping to the plot area instead drops BOTH, which is a different axis from
   * beta's. Six labels, not five and not seven.
   */
  const inPlot = s.ticks.filter((t) => {
    const y = yAt(t);
    return y >= 0 && y <= box.height;
  });

  const area =
    `M ${xAt(0).toFixed(1)},${zeroY.toFixed(1)} `
    + points.map((p, i) => `L ${xAt(i).toFixed(1)},${yAt(p.equity).toFixed(1)}`).join(' ')
    + ` L ${xAt(points.length - 1).toFixed(1)},${zeroY.toFixed(1)} Z`;

  // ⚠ THREE x-labels — first, middle, last — as the old client does. A label per
  // point is unreadable at 500 trades and a label per N is unstable, because the
  // set changes as the range moves.
  const xLabels = points.length > 5
    ? [0, Math.floor(points.length / 2), points.length - 1]
    : [0, points.length - 1].filter((v, i, a) => a.indexOf(v) === i);

  /**
   * ⚠ COUNTS THE TWO KINDS SEPARATELY (#41).
   *
   * This read `${points.length} trades`, which was true while the curve held
   * nothing but trades and became a small lie the moment account adjustments
   * were interleaved — a screen reader would have been told a payout was a
   * trade. The rendered chart cannot show that error; only this string can, so
   * it is the only place the distinction can be got wrong invisibly.
   */
  const adjCount = points.filter((p) => p.kind === 'adj').length;
  const ariaLabel =
    `Cumulative P&L, ${points.length - adjCount} trades`
    + (adjCount ? ` and ${adjCount} account adjustments` : '')
    + `, ending ${fmtY(values[values.length - 1] ?? 0)}`;

  return (
    <div className="eqc" style={{ height }}>
      <svg
        className="eqc-svg"
        viewBox={`0 0 ${box.width} ${box.height}`}
        // ⚠ See the header. This is the decision the rest of the file serves.
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
        // ⚠ On the SVG, not the overlay: leaving across a dot or the line
        // itself would otherwise strand the readout on screen.
        onMouseLeave={() => setHover(null)}
      >
        {/* ── 🚨 `gradientUnits` IS LOAD-BEARING. WITHOUT IT THE COLOUR SPLIT
            LANDS AT THE WRONG HEIGHT ON EVERY CURVE ──

            `zeroFrac` is a fraction of the PLOT AREA. SVG's default
            (`objectBoundingBox`) measures the gradient against the bounding box
            of the shape being painted — the drawn line — not the plot. The two
            coincide only if the curve fills the plot exactly top to bottom, and
            `niceTicks` guarantees it never does: it floors and ceils the domain
            to round numbers, so there is always slack above and below.

            The symptom is not subtle. On a run that never went below break-even,
            the bottom of the line and its shading paint RED — the split sits at
            `zeroFrac` of the LINE's own extent, which is above the true $0 line.
            Worked example: equity 0 → peak with the first point at 10% of peak
            gives zeroFrac 0.946, but the polyline's box spans 0.054–0.857 of the
            plot, so the stop renders at 0.813 and everything under ~15% of peak
            is red. On a $10,000 run, everything below about $1,490.

            Pinning both gradients to the plot area is what beta does
            (app/index.html:27378, 27384) and is the whole fix. See #40. */}
        <defs>
          <linearGradient
            id={`fill-${gid}`} gradientUnits="userSpaceOnUse"
            x1="0" y1={box.padT} x2="0" y2={box.padT + ch}
          >
            <stop offset="0" stopColor="var(--green)" stopOpacity="0.30" />
            <stop offset={zeroFrac} stopColor="var(--green)" stopOpacity="0.04" />
            <stop offset={zeroFrac} stopColor="var(--red)" stopOpacity="0.04" />
            <stop offset="1" stopColor="var(--red)" stopOpacity="0.30" />
          </linearGradient>
          <linearGradient
            id={`line-${gid}`} gradientUnits="userSpaceOnUse"
            x1="0" y1={box.padT} x2="0" y2={box.padT + ch}
          >
            <stop offset="0" stopColor="var(--green)" />
            <stop offset={zeroFrac} stopColor="var(--green)" />
            <stop offset={zeroFrac} stopColor="var(--red)" />
            <stop offset="1" stopColor="var(--red)" />
          </linearGradient>
        </defs>

        {/* Gridlines at the nice ticks. */}
        {inPlot.map((t) => (
          <line
            key={t}
            x1={box.padL} y1={yAt(t)} x2={box.padL + cw} y2={yAt(t)}
            stroke="var(--border)" strokeWidth={1} vectorEffect="non-scaling-stroke"
            opacity={t === 0 ? 0.9 : 0.35}
          />
        ))}

        <path d={area} fill={`url(#fill-${gid})`} />
        <polyline
          points={line} fill="none" stroke={`url(#line-${gid})`} strokeWidth={1.75}
          strokeLinejoin="round"
          // ⚠ Without this the line is several pixels thick horizontally and
          // hairline vertically, because the SVG stretches. See the header.
          vectorEffect="non-scaling-stroke"
        />

        {/* ── ADJUSTMENT MARKERS (#41) ──
            A step in this line that is NOT a trade — a payout, a deposit, the
            balance you started with — is otherwise indistinguishable from an
            unusually good or bad session. Beta marks each one; so does this.

            ⚠ DRAWN AS A SQUARE ROTATED 45°, not a `<circle>`, and not because a
            diamond is prettier: the chart is `preserveAspectRatio="none"`, so a
            circle is painted as an ellipse whose eccentricity changes with the
            container width. Every shape here has to be built from a transform
            that can be un-stretched, which `scale()` on a rect can be and `r` on
            a circle cannot.

            ⚠ `data-equity` is a TEST SEAM as well as a marker hook. It is the
            only way to assert the curve OPENS at a carry-in balance rather than
            at zero — the endpoint alone cannot tell those apart, and the
            endpoint is all the aria-label carries. */}
        {/* ── A DOT PER TRADE (#47) ──

            The line alone says where equity went; it does not say how many
            trades got it there. Two very different weeks — forty small trades
            and three large ones — draw a similar shape, and the dots are the
            only thing on the chart that tells them apart.

            ⚠ GREEN OR RED BY THE POINT’S OWN DELTA, not by the running total.
            A losing trade inside a winning week is a red dot on a line that is
            still above zero, and that disagreement is the information.

            ⚠ THINNED, NOT DROPPED, above ~120 points, which is beta’s rule.
            Five hundred dots on a 900-unit-wide chart is a solid band. The LAST
            point is always drawn whatever the step lands on — it is the one a
            trader looks for, and thinning would hide it exactly when the count
            is high. Hover still reaches every point; only the drawn dots thin.

            🚨 A `<rect>` COUNTER-SCALED, NOT A `<circle>`. This SVG stretches
            (`preserveAspectRatio="none"`), so a circle paints as an ellipse
            whose eccentricity changes with the container width — the same trap
            the adjustment markers below already avoid. Beta uses a real circle
            here and its dots ARE slightly oval; this is a deliberate divergence
            from beta toward the geometry beta intended. */}
        {points.map((p, i) => {
          if (p.kind === 'adj') return null;
          const last = i === points.length - 1;
          if (!last && i % dotStep !== 0) return null;
          const s2 = (box.width / 900);
          return (
            <rect
              key={`dot-${i}`}
              x={-2.4} y={-2.4} width={4.8} height={4.8} rx={2.4}
              transform={`translate(${xAt(i).toFixed(1)} ${yAt(p.equity).toFixed(1)}) scale(${s2.toFixed(3)} 1)`}
              fill={p.delta >= 0 ? 'var(--green)' : 'var(--red)'}
              stroke="var(--bg)" strokeWidth={1} vectorEffect="non-scaling-stroke"
            />
          );
        })}

        {points.map((p, i) => (p.kind === 'adj' ? (
          <rect
            key={`adj-${i}`}
            data-equity={p.equity}
            data-adj={p.label || 'adjustment'}
            x={-3.5} y={-3.5} width={7} height={7}
            transform={`translate(${xAt(i).toFixed(1)} ${yAt(p.equity).toFixed(1)}) rotate(45) scale(${(box.width / 900).toFixed(3)} 1)`}
            fill="var(--purple)"
            stroke="var(--bg)" strokeWidth={1} vectorEffect="non-scaling-stroke"
          >
            <title>{`${p.label || 'Adjustment'} ${fmtY(p.delta)}${p.note ? ` — ${p.note}` : ''}`}</title>
          </rect>
        ) : null))}

        {/* The hovered point, ringed. Same counter-scale as the dots. */}
        {hovered ? (
          <rect
            x={-4} y={-4} width={8} height={8} rx={4}
            transform={`translate(${xAt(hover ?? 0).toFixed(1)} ${yAt(hovered.equity).toFixed(1)}) scale(${(box.width / 900).toFixed(3)} 1)`}
            fill={hovered.equity >= 0 ? 'var(--green)' : 'var(--red)'}
            stroke="var(--bg)" strokeWidth={2} vectorEffect="non-scaling-stroke"
            pointerEvents="none"
          />
        ) : null}

        {/* ── 🚨 ONE TRANSPARENT OVERLAY, NOT A HANDLER PER POINT ──

            Per-point hit areas would leave dead gaps between them, and at 500
            trades they are 1.8 units apart — narrower than a cursor. So a single
            rect covers the plot and the cursor’s x is mapped across it.

            ⚠ THIS WORKS *BECAUSE* THE SVG STRETCHES. The overlay is stretched by
            exactly the same transform as the plot, so its client rect IS the plot
            area in screen pixels and no inverse transform is needed. Beta relies
            on the same property (index.html:27258).

            ⚠ `onMouseLeave` on the SVG, not on the rect: leaving via a dot or the
            line would otherwise leave the readout stuck on screen. */}
        <rect
          data-eq-zone="1"
          x={box.padL} y={box.padT} width={cw} height={ch}
          fill="transparent"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setHover(r.width ? nearestIndex((e.clientX - r.left) / r.width, points.length) : null);
          }}
        />
      </svg>

      {/* ⚠ HTML, NOT SVG <text>. The chart stretches, and stretched text is
          squashed — there is no `vector-effect` for glyphs. Positioned as a
          percentage of the plot box so it tracks the same geometry. */}
      <div className="eqc-ylabels">
        {inPlot.map((t) => (
          <span key={t} style={{ top: `${((yAt(t) / box.height) * 100).toFixed(3)}%` }}>
            {fmtY(t)}
          </span>
        ))}
      </div>
      {/* ── THE READOUT (#47) ──

          HTML, not an SVG `<text>`, for the reason in this file’s header: the
          chart stretches and stretched glyphs are squashed. Positioned as a
          percentage of the same box the axis labels use.

          ⚠ IT FLIPS SIDES past the middle, so the readout for the most recent
          trades — the right-hand end, where a trader spends most of their time
          — does not hang off the edge of the card.

          ⚠ `aria-live` is deliberately ABSENT. This is a mouse affordance for
          information the chart’s `aria-label` already carries in summary;
          announcing every point a cursor passes over would be noise. */}
      {hovered ? (
        <div
          className="eqc-readout"
          data-side={(hover ?? 0) > points.length / 2 ? 'left' : 'right'}
          style={{
            left: `${((xAt(hover ?? 0) / box.width) * 100).toFixed(2)}%`,
            top: `${((yAt(hovered.equity) / box.height) * 100).toFixed(2)}%`,
          }}
        >
          <div className="eqc-readout-total">
            Total <strong>{fmtY(hovered.equity)}</strong>
          </div>
          {hovered.kind === 'adj' ? (
            <>
              <div className="eqc-readout-adj">
                {hovered.label || 'Adjustment'}: {fmtY(hovered.delta)}
              </div>
              {hovered.note ? <div className="eqc-readout-note">{hovered.note}</div> : null}
            </>
          ) : (
            <div className={hovered.delta >= 0 ? 'green' : 'red'}>
              {fmtY(hovered.delta)}
              {hovered.contract ? <span className="eqc-readout-sym">{hovered.contract}</span> : null}
            </div>
          )}
          {/* ⚠ The DATE only when the window spans more than one day — beta’s
              rule. On a single-day chart every point carries the same date and
              repeating it in every readout is noise; the time is what varies. */}
          <div className="eqc-readout-when">
            {multiDay ? `${axisDate(hovered.date)} ` : ''}{hovered.time}
          </div>
        </div>
      ) : null}

      <div className="eqc-xlabels">
        {xLabels.map((i) => (
          <span key={i} style={{ left: `${((xAt(i) / box.width) * 100).toFixed(3)}%` }}>
            {axisDate(points[i]?.date ?? '')}
          </span>
        ))}
      </div>
    </div>
  );
}

/** The old client's padding. Height is overridden per call site. */
const POINTS_BOX = {
  width: 900, height: 260, padL: 64, padR: 20, padT: 16, padB: 36,
};
